import React, { useRef } from 'react';
import { TrendingProject } from '../../types';
import './TrendingPosterView.css';

interface TrendingPosterViewProps {
  projects: TrendingProject[];
  selectedDate: string;
}

const TrendingPosterView: React.FC<TrendingPosterViewProps> = ({ projects, selectedDate }) => {
  const posterRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateStr === today.toISOString().split('T')[0]) {
      return '今天';
    } else if (dateStr === yesterday.toISOString().split('T')[0]) {
      return '昨天';
    } else {
      return date.toLocaleDateString('zh-CN', {
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const getMonthName = (dateStr: string): string => {
    const date = new Date(dateStr);
    const monthNames = ['一月', '二月', '三月', '四月', '五月', '六月',
                       '七月', '八月', '九月', '十月', '十一月', '十二月'];
    return monthNames[date.getMonth()];
  };

  const getDay = (dateStr: string): number => {
    const date = new Date(dateStr);
    return date.getDate();
  };

  const handleDownload = async () => {
    if (!posterRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      // 动态导入html2canvas
      const html2canvas = (await import('html2canvas')).default;

      const canvas = await html2canvas(posterRef.current, {
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        width: posterRef.current.scrollWidth,
        height: posterRef.current.scrollHeight,
        onclone: (clonedDoc: Document) => {
          // 确保克隆的文档中的样式正确应用
          const clonedElement = clonedDoc.querySelector('.trending-poster');
          if (clonedElement) {
            (clonedElement as HTMLElement).style.transform = 'none';
          }
        }
      } as any);

      // 创建下载链接
      const link = document.createElement('a');
      link.download = `GitHub-Trending-${selectedDate}.png`;
      link.href = canvas.toDataURL('image/png', 0.95);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 显示成功提示
      alert('海报下载成功！');
    } catch (error) {
      console.error('下载失败:', error);
      alert('下载失败，请重试');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="trending-poster-container">
      {/* 下载按钮 */}
      <div className="poster-controls">
        <button
          className="download-btn"
          onClick={handleDownload}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              生成中...
            </>
          ) : (
            <>
              <i className="fas fa-download"></i>
              下载海报
            </>
          )}
        </button>
      </div>

      {/* 海报内容 */}
      <div className="trending-poster" ref={posterRef}>
        {/* 海报头部 */}
        <div className="poster-header">
          <div className="poster-title">
            <span className="title-text">GitHub</span>
            <span className="trending-badge">趋势</span>
          </div>
          <div className="poster-date">
            <span className="date-month">{getMonthName(selectedDate)}</span>
            <span className="date-day">{getDay(selectedDate)}</span>
          </div>
        </div>

        {/* 海报副标题 */}
        <div className="poster-subtitle">
          <span className="subtitle-text">See what the GitHub community is most excited about today</span>
          <span className="subtitle-emoji">🔥</span>
        </div>

        {/* 项目列表 */}
        <div className="poster-projects">
          {projects.map((project) => (
            <div key={project.id} className="poster-project-item">
              {/* 项目信息 */}
              <div className="project-content">
                {/* 项目标题 */}
                <div className="project-title">
                  <div className="project-name-with-avatar">
                    {project.owner_avatar && (
                      <img
                        src={project.owner_avatar}
                        alt={project.owner}
                        className="author-avatar"
                      />
                    )}
                    <span className="project-name">{project.full_name}</span>
                  </div>
                  <span className="project-url">{project.url}</span>
                </div>

                {/* 项目描述或AI解读 */}
                <div className="project-description">
                  {project.ai_interpretation || project.description || '暂无描述'}
                </div>

                {/* 项目统计 */}
                <div className="project-stats">
                  <span className="stat-item">
                    <span className="stat-label">{project.language || 'Unknown'}</span>
                  </span>
                  <span className="stat-item">
                    <span className="stat-value">{formatNumber(project.stars)} stars</span>
                  </span>
                  <span className="stat-item">
                    <span className="stat-value">{formatNumber(project.forks)} forks</span>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrendingPosterView;
